import {
    type AfterViewInit,
    Component,
    type ComponentRef,
    Directive,
    inject,
    input,
    type OnDestroy,
    output,
    ViewContainerRef,
} from '@angular/core';
import { IconFieldModule } from '@openng/optimus-ui/iconfield';
import { InputIconModule } from '@openng/optimus-ui/inputicon';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import type { ToolbarPreparingInfo } from 'devextreme/common/grids';
import type { EventInfo } from 'devextreme/events';
import type dxDataGrid from 'devextreme/ui/data_grid';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';

@Component({
    template: `
        <p-iconfield>
            <p-inputicon class="pi pi-search" />
            <input
                pInputText
                type="search"
                [placeholder]="placeholder()"
                [value]="value()"
                class="tw:w-[15rem] tw:md:w-[20rem]"
                (input)="onInput($event)"
            />
        </p-iconfield>
    `,
    imports: [IconFieldModule, InputIconModule, InputTextModule],
})
class InternalPrimeSearchComponent {
    readonly placeholder = input.required<string>();
    readonly value = input('');
    readonly search = output<string>();

    onInput(event: Event) {
        this.search.emit((event.target as HTMLInputElement).value);
    }
}

/**
 * Directive to add primeng search input to the toolbar of a DevExtreme data grid.
 * It will add the search input to the toolbar and keep the PrimeNG input value synced with the DevExtreme search panel value.
 */
@Directive({
    selector: 'dx-data-grid[corGridSearch]',
})
export class CorDxGridSearchDirective implements AfterViewInit, OnDestroy {
    /** The location of the search input in the toolbar. */
    readonly corGridSearchLocation = input<'before' | 'after' | 'center'>('before');
    /** The placeholder for the search input. */
    readonly corGridSearchPlaceholder = input('Search...');

    private readonly dataGrid = inject(DxDataGridComponent);
    private readonly vcr = inject(ViewContainerRef);
    private internalComponentRef?: ComponentRef<InternalPrimeSearchComponent>;

    ngAfterViewInit(): void {
        // Get the data grid instance
        const gridInstance = this.dataGrid?.instance;

        if (!gridInstance) {
            console.error('CorDxGridSearchDirective: Data grid instance not found');
            return;
        }

        // Create PrimeNG search component
        const internalComponentRef = this.vcr.createComponent(InternalPrimeSearchComponent);
        internalComponentRef.setInput('placeholder', this.corGridSearchPlaceholder());
        internalComponentRef.setInput('value', gridInstance.state().searchText || '');

        // Subscribe to the search event and update the data grid instance search text
        // Subscription should be detroyed when directive is destroyed.
        internalComponentRef.instance.search.subscribe(value => {
            gridInstance.searchByText(value);
        });

        this.internalComponentRef = internalComponentRef;

        const existingOnToolbarPreparing = gridInstance.option('onToolbarPreparing');
        gridInstance.option(
            'onToolbarPreparing',
            (e: EventInfo<dxDataGrid<any, any>> & ToolbarPreparingInfo) => {
                if (typeof existingOnToolbarPreparing === 'function') {
                    existingOnToolbarPreparing(e);
                }

                const toolbarItems = e?.toolbarOptions?.items;

                // Already added the corGridSearch so nothing to do now
                if (toolbarItems.some((item: any) => item?.name === 'corGridSearch')) return;

                toolbarItems.unshift({
                    name: 'corGridSearch', // key for custom toolbar item (for later deduping)
                    location: this.corGridSearchLocation(),
                    locateInMenu: 'never',
                    template: () => internalComponentRef.location.nativeElement,
                });
            },
        );

        const existingOptionChanged = gridInstance.option('onOptionChanged');

        // Keep Prime input value synced when DevExtreme restores/updates searchPanel.text.
        gridInstance.option('onOptionChanged', (e: any) => {
            // Call the existing option changed handler if it exists.
            if (typeof existingOptionChanged === 'function') {
                existingOptionChanged(e);
            }

            // If the option changed is not the search panel text, return.
            if (e?.fullName !== 'searchPanel.text') return;
            this.internalComponentRef?.setInput('value', e.value); // Update the Prime input value.
        });
    }

    ngOnDestroy(): void {
        this.internalComponentRef?.destroy();
    }
}
